import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Calendar from 'rc-calendar';
import moment from 'moment-timezone';
import Icon from 'components/Utils/NewIcon';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import CalendarUtils from 'services/CalendarUtils';
import AdminAppointmentDetails from 'components/HealthRecords/Appointments/AdminAppointmentDetails';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import TimeZoneAgnosticBigCalendar from './TimeZoneAgnosticBigCalendar';
import Event from 'components/Schedule/Events';
import DefaultModal from 'components/Utils/DefaultModal';
import './style.scss';
import { useAppSelector } from 'careand-redux/store';
import { Device } from 'services/Native/Device';

const DnDCalendar = withDragAndDrop(TimeZoneAgnosticBigCalendar, {
  backend: false,
});

const ScheduleView = (props) => {
  let selectedDate = getSelectedDate(props);
  return (
    <div className="row full-width schedule full-height">
      {props.showCalendar && <CalendarWrapper {...props} selectedDate={selectedDate} />}
      {props.showAgenda && <Agenda {...props} selectedDate={selectedDate} />}
      {!!props.selectedAppointment && <AppointmentDetailsModal {...props} />}
    </div>
  );
};

ScheduleView.propTypes = {
  moveEvent: PropTypes.func,
  onDateSelect: PropTypes.func,
  onSelectSlot: PropTypes.func,
  closeModal: PropTypes.func,
  events: PropTypes.object,
  selectedDate: PropTypes.object,
  selectedAppointment: PropTypes.object,
  showAgenda: PropTypes.bool,
  showCalendar: PropTypes.bool,
  showAgendaToolbar: PropTypes.bool,
  showArrow: PropTypes.bool,
  agendaTitle: PropTypes.string,
  physicians: PropTypes.array,
  shifts: PropTypes.array,
};

ScheduleView.defaultProps = {
  showAgendaToolbar: true,
  showAgenda: true,
  showCalendar: true,
};

const CalendarWrapper = (props) => (
  <div className="calendar">
    <Calendar
      selectedValue={props.selectedDate}
      showDateInput={false}
      onSelect={props.onDateSelect}
    />
    <span className="select-day">Select a day to view Schedule</span>
  </div>
);

const Agenda = (props) => {
  const isDevice = Device.isMobile();

  return (
    <div className="day-schedule">
      {props.agendaTitle !== undefined && (
        <span className="small-title padded-title arrow-title">
          {props.agendaTitle}
          {props.showArrow && (
            <Icon
              src="arrow-right"
              alt="See full schedule"
              className="arrow"
              height="15px"
              onClick={props.onArrowClick}
            />
          )}
        </span>
      )}
      {(props.events.shifts || []).length > 0 && (
        <DnDCalendarWrapper
          {...props}
          events={props.events.shifts || []}
          className={'shifts-calendar ' + (props.className || '')}
          isDraggable={false}
        />
      )}
      <DnDCalendarWrapper
        {...props}
        events={props.events.events || []}
        className="events-calendar"
        isDraggable={!isDevice}
      />
    </div>
  );
};

const DnDCalendarWrapper = (props) => {
  const isDevice = Device.isMobile();
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);

  const onSelecting = useMemo(() => {
    return isDevice ? () => false : undefined;
  }, [isDevice]);

  const step = props.step || 5;

  return (
    <div className={props.className}>
      <DnDCalendar
        onSelecting={onSelecting}
        selectable
        timeZoneName={CalendarUtils.getTargetTimezone()}
        events={props.events}
        components={{
          event: ({ event }) => (
            <Event
              event={event}
              onClick={event.editable ? props.clickedAppointment : undefined}
              admins={admins}
              physicians={physicians}
            />
          ),
        }}
        step={step}
        eventPropGetter={(event) => {
          let result = {};
          if (event.type === 'shift') result = { className: `my-${event.type}-event` };
          return result;
        }}
        draggableAccessor={(event) =>
          props.isDraggable !== undefined ? props.isDraggable && event.draggable : false
        }
        resizableAccessor={(event) => false}
        timeslots={60 / step}
        defaultView="day"
        toolbar={props.showAgendaToolbar}
        min={CalendarUtils.momentToDate(getMinHour())}
        max={CalendarUtils.momentToDate(getMaxHour())}
        defaultDate={props.selectedDate}
        startAccessor="start"
        endAccessor="end"
        onEventDrop={props.moveEvent}
        date={props.selectedDate}
        onNavigate={props.onDateSelect}
        onSelectSlot={props.onSelectSlot}
      />
    </div>
  );
};

const AppointmentDetailsModal = (props) =>
  props.selectedAppointment !== undefined && (
    <DefaultModal
      className="modal admin-appointment-details-modal"
      key="admin-appointment-details-modal"
      wrap={true}
      padding={false}
      title="Appointment Details"
      content={
        <AdminAppointmentDetails
          appointment={props.selectedAppointment}
          onClose={props.closeModal}
        />
      }
      isOpen={props.selectedAppointment !== undefined}
      onRequestClose={props.closeModal}
    />
  );

function getSelectedDate({ selectedDate }) {
  return CalendarUtils.setToHour(selectedDate, 12);
}

function getMinHour() {
  let min = moment();
  min.hour(8);
  min.minute(0);
  return min;
}

function getMaxHour() {
  let max = moment();
  max.hour(20);
  max.minute(59);
  return max;
}

export default ScheduleView;
